<template>
  <PublicLayout
    variant="no-bg"
    :hero="false"
    breadcrumbs-in-top
    :ui="{
      main: 'pt-0!',
    }"
  >
    <UContainer class="mb-0!">
      <SPBreadcrumbs class="mb-3!" dark :breadcrumbs="breadcrumbs" />

      <div
        class="school-card flex flex-col p-15 max-xl:p-10 max-xl:pt-10! max-lg:p-7 max-md:p-4 w-full h-123 max-lg:h-auto rounded-[24px] mb-16 max-xl:mb-10"
        :style="{
          '--bg-image': `url(${pageData?.image_url})`,
        }"
      >
        <h2 class="text-white typo-heading-h2 mb-4">
          {{ pageData?.name }}
        </h2>

        <p
          class="w-3/7 max-xl:w-7/12 max-lg:w-10/12 max-md:w-full text-white typo-paragraph-md-regular mb-8"
        >
          {{ pageData?.description }}
        </p>

        <UButton
          class="px-4.5 w-max max-lg:mb-15 max-md:w-full"
          @click="signForClass"
        >
          Записаться
        </UButton>

        <div class="mt-auto flex max-lg:flex-col gap-5 max-lg:gap-2">
          <SmallCard
            v-for="(card, index) in cards"
            :key="index + 1"
            :to="card.to"
            :icon="card.icon"
            :top="card.top"
            :bottom="card.bottom"
            :ui="{
              root: 'h-22! max-xl:h-18! w-full! block',
              top: 'text-lg! text-neutral-default! font-medium mb-0!',
              bottom: 'font-lato! text-neutral-light text-sm',
              icon: card.iconClasses,
              iconWrapper: ['text-white!', card.iconWrapper].join(' '),
            }"
          />
        </div>
      </div>

      <DetailTabs
        v-model="activeTab"
        :items="SCHOOL_TABS"
        :content="false"
        tabs-class="w-max mb-10 max-xl:mb-7 max-md:mb-5"
      />
    </UContainer>

    <div class="min-h-100">
      <NuxtPage />
    </div>
  </PublicLayout>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '~/types'
import PublicLayout from '~/layouts/PublicLayout.vue'
import DetailTabs from '~/components/DetailTabs.vue'
import { declOfNumWithVal } from '~/shared'
import SignForClassModal from '~/components/SignForClassModal.vue'
import { useDetailTabs } from '~/composables/useDetailTabs'
import { SCHOOL_TABS } from '~/shared/school'
import { useModal } from '~/shared/modals'

const route = useRoute()
const id = route.params.id as string

defineOptions({
  name: 'SchoolDetail',
})

const { routeId } = useRouteId()

const schoolStore = useSchoolStore()
const { pageData } = storeToRefs(schoolStore)

const breadcrumbs = computed((): BreadcrumbItem[] => [
  {
    name: 'Школы',
    to: '/schools/',
  },
  {
    name: pageData.value?.name ?? '',
  },
])

await useAppAsyncData(`school-${id}`, async () => {
  await Promise.all([schoolStore.fetchSchool(id)])
})

const cards = computed(() => [
  {
    top: declOfNumWithVal(coachesCount.value, [
      'тренер',
      'тренера',
      'тренеров',
    ]),
    bottom: 'Со специальной сертификацией',
    icon: 'i-local-coach',
    iconWrapper: 'bg-success',
    to: `/schools/${routeId}/trainers`,
  },
  {
    top: declOfNumWithVal(directions.value?.length, [
      'направление',
      'направления',
      'направлений',
    ]),
    bottom: 'В данной школе',
    icon: 'i-local-dumbbell',
    iconClasses: '',
    iconWrapper: 'bg-accent-light',
    to: `/schools/${routeId}/directions`,
  },
  {
    top: declOfNumWithVal(studentsCount.value, [
      'ученик',
      'ученика',
      'учеников',
    ]),
    bottom: 'В данной школе',
    icon: 'i-local-users',
    iconWrapper: 'bg-[#03A2AB]!',
    to: `/schools/${routeId}/our-pride`,
  },
])

const directions = computed(() => pageData.value?.directions)
const coachesCount = computed(() => pageData.value?.trainers_count)
const studentsCount = computed(() => pageData.value?.students_count)

const { activeTab } = useDetailTabs({ tabs: SCHOOL_TABS, basePath: '/schools' })

const signForClass = () => {
  const { open } = useModal(SignForClassModal, {
    props: {
      id: routeId,
      apiMethod: schoolStore.trainingSignup,
    },
  })

  open()
}
</script>

<style lang="scss" scoped>
.detail-tabs {
  --ui-text-muted: var(--color-neutral-default);
}

.school-card {
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  background-image:
    linear-gradient(
      274.12deg,
      rgba(4, 17, 1, 0) 37.97%,
      rgba(4, 17, 1, 0.5) 86.42%
    ),
    linear-gradient(
      356.94deg,
      rgba(4, 17, 1, 0) 61.66%,
      rgba(4, 17, 1, 0.5) 96.19%
    ),
    var(--bg-image);
}
</style>
